// -----------------------------------------------------------------------------
// Admin endpoint paths, in one table.
//
// STATUS: BUILT AND WIRED. `Backend/src/modules/admin/` implements every path
// below, mounted at `/api/v1/admin` with `requireMinimumRole(ADMIN)` on the
// whole router and `SUPER_ADMIN` on staff, config, refunds and the audit log.
//
// The shapes follow the brief's §4 rather than the older sketch in
// `Documents/architecture/21-complete-api-list.md`, which spells several of them
// differently (`POST /admin/trainers/{id}/approve` rather than
// `PATCH …/approval`). Where the two disagreed, §4 won and the backend was built
// to match - so this file and `admin.routes.ts` are the pair that must stay in
// step.
//
// They still live here alone: a path change is an edit to this file and its
// route, not a search through twelve screens for string literals.
// -----------------------------------------------------------------------------

export const endpoints = {
  auth: {
    session: '/auth/session',
    me: '/auth/me',

    /**
     * Two-factor staff sign-in. Unauthenticated - these produce a session.
     *
     * The password is checked SERVER-SIDE so no Firebase session exists until
     * the code is verified; `verify` returns a custom token, which is the only
     * credential the panel ever signs in with.
     */
    staff: {
      challenge: '/auth/staff/challenge',
      resend: '/auth/staff/resend',
      verify: '/auth/staff/verify',
    },
  },

  analytics: '/admin/analytics',

  users: {
    list: '/admin/users',
    detail: (id: string) => `/admin/users/${id}`,
    status: (id: string) => `/admin/users/${id}/status`,
    /** DELETE. Soft - see `deleteUser` in `admin.people.service.ts`. */
    remove: (id: string) => `/admin/users/${id}`,
    /** SUPER_ADMIN only. */
    role: (id: string) => `/admin/users/${id}/role`,
  },

  trainers: {
    pending: '/admin/trainers/pending',
    detail: (id: string) => `/admin/trainers/${id}`,
    approval: (id: string) => `/admin/trainers/${id}/approval`,
    /** Puts a coach into the member app's discovery carousel, or takes them out. */
    featured: (id: string) => `/admin/trainers/${id}/featured`,
    /**
     * Corrects or clears the STARTING rating set at approval.
     *
     * The normal path is the approval itself; this exists because a mistyped
     * 5.0 would otherwise be permanent. Refused once the coach has real
     * reviews - at that point the rating is earned, not editorial.
     */
    rating: (id: string) => `/admin/trainers/${id}/rating`,
    /**
     * SUPER_ADMIN only. Permanently erases an application - card, evidence and
     * inbox rows - and soft-deletes the account behind it. Refused for an
     * already-approved coach; those are removed from People instead.
     */
    remove: (id: string) => `/admin/trainers/${id}`,
  },

  moderation: {
    list: '/admin/moderation',
    decide: (id: string) => `/admin/moderation/${id}`,
  },

  /**
   * The account-deletion queue.
   *
   * ⚠ THERE IS NO REJECT ENDPOINT, and the API has none to expose. The account
   * is already closed by the time it appears here and a sweep erases it on a
   * timer regardless - `purge` only brings that forward. See
   * `Backend/src/modules/profile/deletionRequest.service.ts`.
   */
  deletions: {
    list: '/admin/deletions',
    purge: (id: string) => `/admin/deletions/${id}/purge`,
  },

  staff: {
    list: '/admin/staff',
    invite: '/admin/staff',
    detail: (id: string) => `/admin/staff/${id}`,
    reactivate: (id: string) => `/admin/staff/${id}/reactivate`,
  },

  subscriptions: '/admin/subscriptions',

  payments: {
    list: '/admin/payments',
    refund: (id: string) => `/admin/payments/${id}/refund`,
  },

  /** The app's OWN plan catalogue. Coach tiers are not editable here. */
  plans: {
    list: '/admin/plans',
    update: (id: string) => `/admin/plans/${id}`,
  },

  /** Discount codes. The id IS the code, uppercased. */
  coupons: {
    list: '/admin/coupons',
    create: '/admin/coupons',
    update: (code: string) => `/admin/coupons/${code}`,
  },

  tickets: {
    list: '/admin/support-tickets',
    detail: (id: string) => `/admin/support-tickets/${id}`,
    reply: (id: string) => `/admin/support-tickets/${id}/replies`,
  },

  messages: {
    /** Which channels are configured in the API environment. */
    channels: '/admin/messages/channels',
    /** Resolves an audience filter to counts. Required before sending. */
    preview: '/admin/messages/preview',
    /** GET lists campaigns; POST sends one (SUPER_ADMIN). */
    list: '/admin/messages',
    send: '/admin/messages',
  },

  auditLogs: '/admin/audit-logs',

  config: '/admin/config',

  /** ADMIN, not SUPER_ADMIN - see the note on `config` above vs this one in `admin.routes.ts`. */
  certificateTypes: '/admin/config/certificate-types',
  skills: '/admin/config/skills',
} as const;
